import { useRef } from 'react';
import Countdown from 'react-countdown';
import confetti from 'canvas-confetti';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionDivider from './SectionDivider';

export default function CountdownSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const confettiFired = useRef(false);
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'centered';

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text ? [{ text: content.cta_text, link: content.cta_link || '#', style: 'solid', icon: '', bg_color: '', text_color: '' }] : [];

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const targetDate = content.target_date ? new Date(content.target_date) : new Date();

    const unitLabels = {
        days: content.days_label || 'Dias',
        hours: content.hours_label || 'Horas',
        minutes: content.minutes_label || 'Minutos',
        seconds: content.seconds_label || 'Segundos',
    };

    // ── Banner variant ──
    if (variant === 'banner') {
        return (
            <section className="relative" style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 ${anim}`}>
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="text-center md:text-left">
                            {content.title && <h2 className="text-xl md:text-2xl font-bold" style={{ color: s.text }}>{content.title}</h2>}
                            {content.subtitle && <p className="text-sm mt-1" style={{ color: s.textSub }}>{content.subtitle}</p>}
                        </div>
                        <Countdown date={targetDate} renderer={({ days, hours, minutes, seconds, completed }) => {
                            if (completed) {
                                if (!confettiFired.current) { confettiFired.current = true; confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, disableForReducedMotion: true }); }
                                return <span className="text-xl font-bold" style={{ color: s.accent }}>{content.started_text || 'El evento ha comenzado'}</span>;
                            }
                            const units = [{ v: days, l: unitLabels.days }, { v: hours, l: unitLabels.hours }, { v: minutes, l: unitLabels.minutes }, { v: seconds, l: unitLabels.seconds }];
                            return (
                                <div className="flex items-center gap-4">
                                    {units.map(({ v, l }, i) => (
                                        <div key={i} className="text-center">
                                            <div className="text-2xl md:text-3xl font-bold tabular-nums" style={{ color: s.accent }}>{String(v).padStart(2, '0')}</div>
                                            <div className="text-[10px] uppercase tracking-wider font-medium mt-0.5" style={{ color: s.textSub }}>{l}</div>
                                        </div>
                                    ))}
                                </div>
                            );
                        }} />
                        {sectionButtons.map((btn, i) => {
                            const btnStyle = btn.style || 'solid';
                            const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                            const lp = resolveLinkProps(btn.link || '#');
                            const customBg = btn.bg_color;
                            const customText = btn.text_color;
                            let cls = 'shrink-0 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                            let style = {};
                            if (btnStyle === 'solid' || btnStyle === 'pill') {
                                if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-xl'; }
                                else { cls += 'text-white shadow-lg hover:shadow-xl'; style.backgroundColor = s.accent; }
                            } else if (btnStyle === 'outline') {
                                if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                else { style.color = s.accent; style.borderColor = s.accent; cls += 'border-2 hover:opacity-80'; }
                            } else { style.color = customText || s.accent; cls += 'hover:opacity-80'; }
                            if (btnStyle === 'pill') cls = cls.replace('rounded-lg', 'rounded-full');
                            if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                            return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Centered variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                {content.title && <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: s.text }}>{content.title}</h2>}
                {content.subtitle && <p className="text-base md:text-lg mb-10" style={{ color: s.textSub }}>{content.subtitle}</p>}

                <Countdown date={targetDate} renderer={({ days, hours, minutes, seconds, completed }) => {
                    if (completed) {
                        if (!confettiFired.current) { confettiFired.current = true; confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, disableForReducedMotion: true }); }
                        return <p className="text-2xl font-bold" style={{ color: s.accent }}>{content.started_text || 'El evento ha comenzado'}</p>;
                    }
                    const units = [{ v: days, l: unitLabels.days }, { v: hours, l: unitLabels.hours }, { v: minutes, l: unitLabels.minutes }, { v: seconds, l: unitLabels.seconds }];
                    return (
                        <div className="flex items-center justify-center gap-4 md:gap-8">
                            {units.map(({ v, l }, i) => (
                                <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${200 + i * 100}ms` : '0ms' }}>
                                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl flex items-center justify-center mb-2"
                                        style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                        <span className="text-3xl md:text-5xl font-bold tabular-nums" style={{ color: s.accent }}>{String(v).padStart(2, '0')}</span>
                                    </div>
                                    <span className="text-xs md:text-sm uppercase tracking-wider font-medium" style={{ color: s.textSub }}>{l}</span>
                                </div>
                            ))}
                        </div>
                    );
                }} />

                {sectionButtons.length > 0 && (
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        {sectionButtons.map((btn, i) => {
                            const btnStyle = btn.style || 'solid';
                            const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                            const lp = resolveLinkProps(btn.link || '#');
                            const customBg = btn.bg_color;
                            const customText = btn.text_color;
                            let cls = 'px-8 py-3.5 rounded-xl font-semibold text-base transition-all hover:scale-105 inline-flex items-center gap-2 ';
                            let style = {};
                            if (btnStyle === 'solid' || btnStyle === 'pill') {
                                if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-xl'; }
                                else { cls += 'text-white shadow-lg'; style.backgroundColor = s.accent; }
                            } else if (btnStyle === 'outline') {
                                if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                else { style.color = s.accent; style.borderColor = s.accent; cls += 'border-2 hover:opacity-80'; }
                            } else { style.color = customText || s.accent; cls += 'hover:opacity-80'; }
                            if (btnStyle === 'pill') cls = cls.replace('rounded-xl', 'rounded-full');
                            if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                            return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                        })}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
